import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { useTheme } from "../context/ThemeContext";
import API_BASE_URL from "../config";
import useAgentData from "../hooks/useAgentData";

const AddCustomerForAgentModal = ({ isOpen, onClose, onCustomerAdded }) => {
  const { canAccessMarmurist } = useAgentData();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { darkMode } = useTheme();
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [type, setType] = useState(0);

  useEffect(() => {
    if (!canAccessMarmurist) {
      setType(0); // Ha nincs jogosultság, mindig ClientFinal legyen
    }
  }, [canAccessMarmurist]);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setPhone("");
      setCity("");
      setCounty("");
      setBusinessType("");
      setNotes("");
      setSuccessMessage("");
      setError(null);
      setNameError("");
      setPhoneError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    let valid = true;

    if (!name.trim()) {
      setNameError("Numele este obligatoriu!");
      valid = false;
    }

    if (!phone.match(/^\+?\d{7,15}$/)) {
      setPhoneError("Introduceți un număr de telefon valid!");
      valid = false;
    }

    if (!valid) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          phone,
          city,
          county,
          businessType,
          notes,
          type: parseInt(type, 10),
          status: 0,
          refusalReason: ""
        }),
      });

      if (!response.ok) {
        throw new Error("Eroare la adăugarea clientului.");
      }

      setSuccessMessage("✅ Client adăugat cu succes!");
      setTimeout(() => {
        setSuccessMessage("");
        onCustomerAdded();
        onClose();
      }, 500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div
      id="modal-overlay"
      className={`fixed inset-0 z-[9999] flex items-center justify-center ${
        darkMode ? "bg-black bg-opacity-80" : "bg-gray-500 bg-opacity-50"
      }`}
      onClick={handleOverlayClick}
    >
      <div
        className={`p-6 rounded-3xl shadow-xl w-[600px] relative ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-black"
        }`} // 🔥 Modal háttér és szöveg dark mode szerint
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-red-500 hover:text-red-700 text-2xl"
        >
          ✖
        </button>

        <h2
          className={`text-2xl font-semibold mb-4 text-center ${
            darkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          Adăugare Client Nou
        </h2>

        {successMessage && (
          <p className="text-green-500 bg-green-900 p-2 rounded mb-3 text-center">
            {successMessage}
          </p>
        )}
        {error && (
          <p className="text-red-500 bg-red-900 p-2 rounded mb-3 text-center">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          {[
            {
              label: "Nume",
              placeholder: "Introduceți numele clientului",
              value: name,
              setValue: setName,
              error: nameError,
              required: true,
            },
            {
              label: "Nr. Telefon",
              placeholder: "Introduceți numărul de telefon",
              value: phone,
              setValue: setPhone,
              error: phoneError,
              required: true,
            },
            {
              label: "Oraș",
              placeholder: "Introduceți orașul",
              value: city,
              setValue: setCity,
            },
            {
              label: "Județ",
              placeholder: "Introduceți județul",
              value: county,
              setValue: setCounty,
            },
            {
              label: "Tip Afacere",
              placeholder: "Introduceți tipul de afacere",
              value: businessType,
              setValue: setBusinessType,
            },
          ].map(
            (
              { label, placeholder, value, setValue, error, required },
              index
            ) => (
              <div key={index}>
                <label
                  className={`block font-semibold mb-1 ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {label}
                  {required && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  placeholder={placeholder} // 🔥 Placeholder-ek megmaradtak
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className={`w-full p-2 border rounded-2xl ${
                    darkMode
                      ? "bg-gray-800 text-white placeholder-gray-400 border-gray-600"
                      : "bg-gray-100 text-black placeholder-gray-600 border-gray-300"
                  }`}
                />
                {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
              </div>
            )
          )}

          {/* 🔥 Csak akkor jelenítjük meg a "Tip Client" mezőt, ha a user jogosult rá */}
          {canAccessMarmurist && (
            <div>
              <label
                className={`block font-semibold mb-1 ${
                  darkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Tip Client<span className="text-red-500">*</span>
              </label>
              <select
                value={type}
                onChange={(e) => setType(parseInt(e.target.value, 10))}
                className={`w-full p-2 border rounded-2xl ${
                  darkMode
                    ? "bg-gray-800 text-white border-gray-600"
                    : "bg-gray-100 text-black border-gray-300"
                }`}
              >
                <option value="0">Client Final</option>
                <option value="1">Client Marmist</option>
              </select>
            </div>
          )}
          
          <div className="col-span-2">
            <label
              className={`block font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Notițe
            </label>
            <textarea
              placeholder="Introduceți notițe"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full p-2 border rounded-2xl resize-none h-24 ${
                darkMode
                  ? "bg-gray-800 text-white placeholder-gray-400 border-gray-600"
                  : "bg-gray-100 text-black placeholder-gray-600 border-gray-300"
              }`}
            ></textarea>
          </div>

          <div className="col-span-2 flex justify-between gap-4 mt-4">
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: darkMode ? "#1565c0" : "#1976d2",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                color: "white",
                flex: 1,
                "&:hover": { bgcolor: darkMode ? "#0d47a1" : "#1565c0" },
              }}
              disabled={loading}
            >
              {loading ? "Se încarcă..." : "Adaugă Client"}
            </Button>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: darkMode ? "#fff" : "#000",
                borderColor: darkMode ? "#fff" : "#000",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { borderColor: darkMode ? "#ccc" : "#bbb" },
              }}
            >
              Închide
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerForAgentModal;
